// ============================================================================
// CLEAN NOTIFICATION DROPDOWN WITH FOCUSED IMAGE SERVICE
// File: frontend/src/components/dashboard/NotificationDropdown.tsx
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  hrNotificationService, 
  HRNotification, 
  NotificationStats 
} from '../../services/hrNotification.service';
import { 
  employeeImageService,
  EmployeeImageData
} from '../../services/employeeImage.service'; // 🆕 NEW FOCUSED SERVICE

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (page: string, employeeId?: string) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ 
  isOpen, 
  onClose,
  onNavigate
}) => {
  const [notifications, setNotifications] = useState<HRNotification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    total: 0,
    unread: 0,
    high: 0,
    today: 0,
    thisWeek: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  
  // 🆕 CLEAN: Employee images map
  const [employeeImages, setEmployeeImages] = useState<Map<string, EmployeeImageData>>(new Map());

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      loadNotifications();
      setupRealTimeListener();
    }
  }, [isOpen]);

  // 🆕 CLEAN: Load employee images when notifications change
  useEffect(() => {
    if (notifications.length > 0) {
      loadEmployeeImages();
    }
  }, [notifications]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const [notificationsData, statsData] = await Promise.all([
        hrNotificationService.getRecentNotifications(20),
        hrNotificationService.getNotificationStats()
      ]);

      setNotifications(notificationsData);
      setStats(statsData);

    } catch (error) {
      console.error('Error loading notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const setupRealTimeListener = () => {
    const unsubscribe = hrNotificationService.onNotifications(
      (newNotifications) => {
        setNotifications(newNotifications);
        hrNotificationService.getNotificationStats().then(setStats);
      },
      (error) => {
        console.error('Real-time notifications error:', error);
        setError('Real-time updates failed');
      }
    );

    return () => unsubscribe();
  };

  // 🆕 CLEAN: Load employee images using focused service
  const loadEmployeeImages = async () => {
    try {
      const uniqueEmployeeIds = [...new Set(notifications.map(n => n.employeeId))];
      console.log('🖼️ Loading images for employees:', uniqueEmployeeIds);
      
      // Use batch method from focused service
      const imageMap = await employeeImageService.getMultipleEmployeeImages(uniqueEmployeeIds);
      setEmployeeImages(imageMap);
      
      console.log('✅ Loaded images for', imageMap.size, 'employees');
    } catch (error) {
      console.error('❌ Error loading employee images:', error);
    }
  };

  // 🆕 CLEAN: Get employee avatar using focused service
  const getEmployeeAvatar = (employeeId: string, fallbackName: string): string => {
    const imageData = employeeImages.get(employeeId);
    
    if (imageData) {
      return employeeImageService.getAvatarUrl(imageData, 44);
    }
    
    // Fallback if not loaded yet
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}&background=14b8a6&color=fff&size=44&rounded=true&font-size=0.6`;
  };

  // Handle notification click
  const handleNotificationClick = useCallback(async (notification: HRNotification) => {
    try {
      // Mark as read
      if (!notification.isRead) {
        await hrNotificationService.markAsRead(notification.id);
        setNotifications(prev => 
          prev.map(n => 
            n.id === notification.id ? { ...n, isRead: true } : n
          )
        );
        setStats(prev => ({ ...prev, unread: Math.max(0, prev.unread - 1) }));
      }

      // Close dropdown
      onClose();

      // Navigate to leave management
      if (onNavigate) {
        onNavigate('leave');
      } else {
        console.warn('No navigation function provided to NotificationDropdown');
      }
      
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  }, [onNavigate, onClose]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await hrNotificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setStats(prev => ({ ...prev, unread: 0 }));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  }, []);

  const handleViewAllClick = useCallback(() => {
    onClose();
    if (onNavigate) {
      onNavigate('leave');
    } else {
      console.warn('No navigation function provided to NotificationDropdown');
    }
  }, [onNavigate, onClose]);

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getActionColor = (notification: HRNotification) => {
    if (notification.action === 'approved') return '#059669';
    if (notification.action === 'rejected') return '#dc2626';
    if (notification.action === 'overdue') return '#d97706';
    return '#14b8a6';
  };

  const getActionIcon = (notification: HRNotification) => {
    if (notification.action === 'approved') return '✅';
    if (notification.action === 'rejected') return '❌';
    if (notification.action === 'overdue') return '⏰';
    return '📋';
  };

  if (!isOpen) return null;

  return (
    <div 
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: 'calc(100% + 12px)',
        right: '0',
        width: '420px',
        maxWidth: '95vw',
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        zIndex: 1000,
        maxHeight: '500px',
        overflow: 'hidden',
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        animation: 'slideInFade 0.2s ease-out',
        transformOrigin: 'top right'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px',
        background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0f766e 100%)',
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          position: 'relative',
          zIndex: 1
        }}>
          <div>
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              margin: '0 0 4px 0',
              letterSpacing: '-0.025em'
            }}>
              Notifications
            </h3>
            <p style={{
              fontSize: '14px',
              margin: '0',
              opacity: '0.9',
              fontWeight: '400'
            }}>
              {stats.unread > 0 ? `${stats.unread} new updates` : 'You\'re all caught up!'}
            </p>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {stats.unread > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: '500',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)'
                }}
              >
                Mark all read
              </button>
            )}
            
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                color: '#ffffff',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: '400',
                transition: 'all 0.2s ease'
              }}
            >
              ×
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ 
        overflowY: 'auto', 
        maxHeight: '380px',
        backgroundColor: '#ffffff'
      }}>
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            flexDirection: 'column'
          }}>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #f3f4f6',
              borderTop: '3px solid #14b8a6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <span style={{ 
              marginTop: '12px', 
              color: '#6b7280',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Loading notifications...
            </span>
          </div>
        ) : error ? (
          <div style={{
            padding: '32px 24px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>😕</div>
            <p style={{ 
              margin: '0 0 12px 0', 
              color: '#ef4444',
              fontWeight: '500',
              fontSize: '16px'
            }}>
              Oops! Something went wrong
            </p>
            <p style={{ 
              margin: '0 0 16px 0', 
              color: '#6b7280',
              fontSize: '14px'
            }}>
              {error}
            </p>
            <button 
              onClick={loadNotifications}
              style={{
                background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Try Again
            </button>
          </div>
        ) : notifications.length === 0 ? (
          <div style={{
            padding: '48px 24px',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '24px'
            }}>
              🎉
            </div>
            <h4 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              margin: '0 0 8px 0',
              color: '#111827'
            }}>
              All caught up!
            </h4>
            <p style={{ 
              fontSize: '14px', 
              margin: '0',
              color: '#6b7280',
              lineHeight: '1.5'
            }}>
              No new notifications right now.<br/>
              We'll let you know when something happens.
            </p>
          </div>
        ) : (
          <div style={{ padding: '8px 0' }}>
            {notifications.map((notification, index) => (
              <div
                key={notification.id}
                style={{
                  padding: '16px 20px',
                  cursor: 'pointer',
                  backgroundColor: hoveredId === notification.id 
                    ? (!notification.isRead ? '#e6fffa' : '#f9fafb')
                    : (!notification.isRead ? '#f0fdfa' : '#ffffff'),
                  borderLeft: !notification.isRead ? '4px solid #14b8a6' : '4px solid transparent',
                  transition: 'all 0.2s ease',
                  position: 'relative',
                  borderBottom: index < notifications.length - 1 ? '1px solid #f3f4f6' : 'none'
                }}
                onClick={() => handleNotificationClick(notification)}
                onMouseEnter={() => setHoveredId(notification.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* Notification Layout */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  {/* 🆕 CLEAN: Employee avatar using focused service */}
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={getEmployeeAvatar(notification.employeeId, notification.employeeName)}
                      alt={notification.employeeName}
                      style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        border: '2px solid #ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                        objectFit: 'cover'
                      }}
                      onError={(e) => {
                        // Fallback on image load error
                        const target = e.target as HTMLImageElement;
                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(notification.employeeName)}&background=14b8a6&color=fff&size=44&rounded=true&font-size=0.6`;
                      }}
                    />
                    {/* Action badge */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-2px',
                      right: '-2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: getActionColor(notification),
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      border: '2px solid #ffffff',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)'
                    }}>
                      {getActionIcon(notification)}
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <p style={{
                        fontSize: '15px',
                        fontWeight: !notification.isRead ? '600' : '500',
                        color: '#111827',
                        margin: '0',
                        lineHeight: '1.4',
                        flex: 1
                      }}>
                        <span style={{ fontWeight: '700' }}>{notification.employeeName}</span>
                        {notification.action === 'approved' && (
                          <span style={{ fontWeight: '400' }}>'s leave was approved by {notification.lineManagerName}</span>
                        )}
                        {notification.action === 'rejected' && (
                          <span style={{ fontWeight: '400' }}>'s leave was rejected by {notification.lineManagerName}</span>
                        )}
                        {notification.action === 'overdue' && (
                          <span style={{ fontWeight: '400' }}>'s leave is pending approval</span>
                        )}
                      </p>
                      
                      <span style={{
                        fontSize: '12px',
                        color: '#9ca3af',
                        fontWeight: '500',
                        flexShrink: 0,
                        marginLeft: '8px'
                      }}>
                        {getRelativeTime(notification.createdAt)}
                      </span>
                    </div>

                    {/* Details */}
                    <div style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      marginBottom: '8px',
                      lineHeight: '1.4'
                    }}>
                      {notification.leaveType} • {notification.totalDays} day{notification.totalDays > 1 ? 's' : ''}
                      {notification.priority === 'high' && (
                        <span style={{ 
                          color: '#dc2626', 
                          fontWeight: '700',
                          marginLeft: '8px'
                        }}>
                          • High Priority
                        </span>
                      )}
                    </div>

                    {/* Action button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        style={{
                          background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
                          color: '#ffffff',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNotificationClick(notification);
                        }}
                      >
                        View Details
                      </button>
                      
                      {!notification.isRead && (
                        <div style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: '#14b8a6',
                          borderRadius: '50%',
                          animation: 'pulse 2s infinite'
                        }}></div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div style={{
          padding: '16px 20px',
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button 
            onClick={handleViewAllClick}
            style={{
              width: '100%',
              background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 50%, #0f766e 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '12px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(20, 184, 166, 0.3)'
            }}
          >
            View All in Leave Management
          </button>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes slideInFade {
            0% { 
              opacity: 0; 
              transform: translateY(-10px) scale(0.95); 
            }
            100% { 
              opacity: 1; 
              transform: translateY(0) scale(1); 
            }
          }
          
          @keyframes pulse {
            0%, 100% { 
              opacity: 1; 
              transform: scale(1); 
            }
            50% { 
              opacity: 0.5; 
              transform: scale(1.2); 
            }
          }
        `}
      </style>
    </div>
  );
};

export default NotificationDropdown;    